import { useMemo } from 'react';

import { StoreItemModalsProps } from '#components/organisms/modals/StoreItemModals/StoreItemModals.js';
import { TicketedEventsModalsCard } from '#components/organisms/modals/TicketedEventsModals.js';
import { useShopProvider } from '#provider/useShopProvider.js';
import { SeasonTicketStoreItem, useListTickets, useShowEvent } from '@givebacks/api/store';
import { ModalProps, Skeleton, Stack, Title } from 'ui';

import { CheckoutButtonsProps } from '../Cart/buttons/CheckoutButtons/CheckoutButtons';

type SeasonTicketsSectionProps = {
  eventId?: string;
  causeId?: string;
  title?: React.ReactNode;
  modalProps?: Partial<Omit<ModalProps, 'opened' | 'onClose' | 'title'>>;
  checkoutButtonsProps?: Partial<CheckoutButtonsProps>;
  additionalFieldsProps?: StoreItemModalsProps['additionalFieldsProps'];
};

const SeasonTicketsSection = ({
  eventId,
  causeId,
  title,
  modalProps,
  checkoutButtonsProps,
  additionalFieldsProps,
}: SeasonTicketsSectionProps) => {
  const { previewMode } = useShopProvider();
  const { data: tickets, isLoading } = useListTickets({
    eventId,
    causeId,
    ticketsType: 'season_passes',
    queryOptions: {
      join: 'AND',
      searches: previewMode
        ? // if preview mode is true, don't filter by live tickets
          {}
        : {
            live: { value: true },
          },
      order: {
        field: 'order',
        direction: 'asc',
      },
      options: {
        include: 'inventory_summary',
      },
    },
  });

  const { data: event } = useShowEvent({ eventId, causeId });

  const ticketsList = useMemo(() => {
    if (!tickets?.store_items?.length && !isLoading) return [];

    return tickets?.store_items
      ?.map((ticket: SeasonTicketStoreItem) => {
        const endDate = ticket.ticket_window_data?.end?.timestamp;
        const isExpired = endDate && new Date(endDate) < new Date();

        const eventDates = ticket.event_dates_ids.includes('*') ? 'all' : 'multi';

        if (isExpired) return null;

        return (
          <Skeleton key={ticket.uuid} visible={isLoading}>
            <TicketedEventsModalsCard
              storeId={causeId ?? ''}
              storeItemId={ticket.uuid}
              eventId={event?.uuid ?? ''}
              eventTitle={event?.name ?? ''}
              title={ticket.name}
              description={ticket.description}
              price={ticket.price}
              sellDate={ticket.starting_at}
              eventDate={eventDates}
              eventDateId={''}
              includedEventDates={ticket.event_dates_ids}
              inventoryAvailable={ticket.unlimited ? false : ticket.inventory_summary?.amount}
              checkoutButtonsProps={checkoutButtonsProps}
              {...modalProps}
              additionalFieldsProps={additionalFieldsProps}
            />
          </Skeleton>
        );
      })
      .filter(Boolean);
  }, [
    tickets,
    isLoading,
    causeId,
    event?.uuid,
    event?.name,
    checkoutButtonsProps,
    modalProps,
    additionalFieldsProps,
  ]);

  return (
    !!ticketsList?.length && (
      <Stack gap="md">
        <Skeleton visible={isLoading}>{title ?? <Title order={2}>Season Tickets</Title>}</Skeleton>
        <Stack>{ticketsList}</Stack>
      </Stack>
    )
  );
};

export { SeasonTicketsSection };
